import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, EyeOff, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { inputCls } from "./Fields";

export function EnquiriesTab() {
  const qc = useQueryClient();
  const { data: enquiries = [] } = useQuery({
    queryKey: ["enquiries"],
    queryFn: async () => (await api.get("/enquiries")).data,
  });
  const markRead = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/enquiries/${id}`, { status })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["enquiries"] }),
  });

  if (enquiries.length === 0)
    return (
      <p data-testid="enquiries-empty" className="text-zinc-300 text-sm border border-dashed border-white/15 p-12 text-center">
        No enquiries yet.
      </p>
    );

  return (
    <div className="space-y-4" data-testid="enquiries-list">
      {enquiries.map((e) => (
        <div key={e.id} className={`border p-6 ${e.status === "new" ? "border-mauve/40 bg-mauve/5" : "border-white/10 bg-surface"}`}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <p className="font-heading font-bold text-silver">
              {e.name} <span className="text-zinc-300 font-normal">· {e.email}</span>
              {e.company && <span className="text-zinc-300 font-normal"> · {e.company}</span>}
            </p>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[10px] tracking-widest uppercase text-mauve border border-mauve/40 px-2 py-0.5">{e.interest}</span>
              <button
                data-testid={`enquiry-toggle-${e.id}`}
                onClick={() => markRead.mutate({ id: e.id, status: e.status === "new" ? "read" : "new" })}
                className="text-zinc-300 hover:text-white transition-colors duration-200"
                title={e.status === "new" ? "Mark read" : "Mark unread"}
              >
                {e.status === "new" ? <Eye size={16} /> : <EyeOff size={16} />}
              </button>
            </div>
          </div>
          <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{e.message}</p>
          <p className="mt-3 font-mono text-[10px] tracking-widest uppercase text-zinc-400">{new Date(e.created_at).toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
}

export function CommunityTab() {
  const qc = useQueryClient();
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const { data: posts = [] } = useQuery({
    queryKey: ["community"],
    queryFn: async () => (await api.get("/community")).data,
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/community/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["community"] });
      toast.success("Thread removed");
    },
  });
  const staffReply = useMutation({
    mutationFn: async (id) => (await api.post(`/community/${id}/replies`, { message: replyText.trim() })).data,
    onSuccess: () => {
      setReplyFor(null);
      setReplyText("");
      qc.invalidateQueries({ queryKey: ["community"] });
      toast.success("Staff reply posted");
    },
  });

  if (posts.length === 0)
    return (
      <p data-testid="community-admin-empty" className="text-zinc-300 text-sm border border-dashed border-white/15 p-12 text-center">
        No community threads yet.
      </p>
    );

  return (
    <div className="space-y-4" data-testid="community-admin-list">
      {posts.map((p) => (
        <div key={p.id} className="border border-white/10 bg-surface p-6">
          <div className="flex items-center justify-between gap-3 mb-2">
            <p className="font-heading font-bold text-silver">{p.name}</p>
            <button
              data-testid={`community-delete-${p.id}`}
              onClick={() => del.mutate(p.id)}
              className="text-zinc-300 hover:text-red-400 transition-colors duration-200"
              title="Delete thread"
            >
              <Trash2 size={16} />
            </button>
          </div>
          <p className="text-sm text-zinc-300 whitespace-pre-wrap">{p.message}</p>
          {p.replies?.map((r) => (
            <div key={r.id} className="mt-3 border-l-2 border-mauve/30 pl-4">
              <p className="text-xs text-zinc-300">
                {r.name} {r.is_staff && <span className="text-mauve">(staff)</span>}
              </p>
              <p className="text-sm text-zinc-200">{r.message}</p>
            </div>
          ))}
          {replyFor === p.id ? (
            <div className="mt-4">
              <textarea
                data-testid={`staff-reply-input-${p.id}`}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                rows={2}
                placeholder="Reply as Klyx™…"
                className={`${inputCls} resize-none`}
              />
              <div className="mt-2 flex gap-2">
                <button
                  data-testid={`staff-reply-submit-${p.id}`}
                  disabled={!replyText.trim() || staffReply.isPending}
                  onClick={() => staffReply.mutate(p.id)}
                  className="bg-mauve text-white px-4 py-2 text-[11px] tracking-[0.2em] uppercase font-bold hover:bg-mauve-dark transition-colors duration-200 disabled:opacity-40"
                >
                  Post staff reply
                </button>
                <button
                  onClick={() => { setReplyFor(null); setReplyText(""); }}
                  className="border border-white/15 text-zinc-200 px-4 py-2 text-[11px] tracking-[0.2em] uppercase hover:text-white transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              data-testid={`staff-reply-open-${p.id}`}
              onClick={() => setReplyFor(p.id)}
              className="mt-4 text-[11px] font-mono tracking-[0.2em] uppercase text-mauve hover:text-white transition-colors duration-200"
            >
              Reply as staff →
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
